import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getMaxWidthForImages, getScrollObject } from './helpers';

const TwoLineCards = ({ children, enableAutoScroll, enableSmoothScroll, autoScrollTimer }) => {
  const containerRef = useRef();
  const scrollableRef = useRef();
  const autoScrollRef = useRef();
  const [shouldAutoScroll, setShouldAutoScroll] = useState(false);
  const [buttonDisplay, setButtonDisplays] = useState({ displayLeft: false, displayRight: true });
  const [containerStyle, setContainerStyle] = useState({});
  const scrollBehaviour = enableSmoothScroll ? 'smooth' : 'instant';

  useEffect(() => {
    if (containerRef && containerRef.current) {
      const { offsetWidth } = containerRef.current;
      const maxWidth = getMaxWidthForImages(offsetWidth);
      setContainerStyle({ maxWidth });
    }
  }, []);

  useEffect(() => {
    const {
      current: { offsetWidth, scrollWidth }
    } = scrollableRef;
    if (scrollWidth <= offsetWidth) {
      setButtonDisplays({ displayLeft: false, displayRight: false });
    }
    setShouldAutoScroll(enableAutoScroll);
  }, [enableAutoScroll]);

  useEffect(() => {
    autoScrollRef.current = shouldAutoScroll;
    const id = setInterval(scrollCheck, autoScrollTimer);
    function scrollCheck() {
      if (!autoScrollRef.current) {
        clearInterval(id);
      } else {
        const scrollOptions = getScrollObject({ forward: true, scrollableRef, scrollBehaviour });
        scrollableRef.current.scrollBy(scrollOptions);
      }
    }

    return () => clearInterval(id);
  }, [autoScrollTimer, scrollBehaviour, shouldAutoScroll]);

  const handleMouseEnter = () => {
    setShouldAutoScroll(false);
  };

  const handleMouseLeave = () => {
    setShouldAutoScroll(enableAutoScroll);
  };

  const handleScrollButtonClick = ({ forward }) => {
    const scrollOptions = getScrollObject({ forward, scrollableRef, scrollBehaviour });
    scrollableRef.current.scrollBy(scrollOptions);
  };

  const updateButtonDisplay = () => {
    const {
      current: { offsetWidth, scrollLeft, scrollWidth }
    } = scrollableRef;
    const updatedDisplayLeft = !!scrollLeft;
    const updatedDisplayRight = scrollLeft + offsetWidth + 1 < scrollWidth;
    setButtonDisplays({ displayLeft: updatedDisplayLeft, displayRight: updatedDisplayRight });
  };

  const { displayLeft, displayRight } = buttonDisplay;

  return (
    <div
      ref={containerRef}
      style={containerStyle}
      className="sc-two-line-cards"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}>
      {displayLeft && (
        <div className="sc-two-line-cards__scroll-button-wrapper">
          <button
            type="button"
            className="sc-two-line-cards__scroll-button-wrapper__button"
            onClick={() => {
              handleScrollButtonClick({ forward: false });
            }}>
            <div className="sc-two-line-cards__scroll-button-wrapper__button__arrow-left" />
          </button>
        </div>
      )}
      <div
        ref={scrollableRef}
        onScroll={updateButtonDisplay}
        className="sc-two-line-cards__content">
        {children}
      </div>
      {displayRight && (
        <div className="sc-two-line-cards__scroll-button-wrapper">
          <button
            type="button"
            className="sc-two-line-cards__scroll-button-wrapper__button"
            onClick={() => {
              handleScrollButtonClick({ forward: true });
            }}>
            <div className="sc-two-line-cards__scroll-button-wrapper__button__arrow-right" />
          </button>
        </div>
      )}
    </div>
  );
};

TwoLineCards.propTypes = {
  enableAutoScroll: PropTypes.bool,
  enableSmoothScroll: PropTypes.bool,
  autoScrollTimer: PropTypes.number,
  children: PropTypes.array
};

TwoLineCards.defaultProps = {
  enableAutoScroll: false,
  enableSmoothScroll: false,
  autoScrollTimer: null,
  children: null
};

export default TwoLineCards;
